import { formatDate } from "@progress/kendo-intl";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { LoadingIndicator } from "../../../core/loading-indicator.component";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import ZoneRoutewiseAbstractModel from "../../../models/reports/zoneroutewiseabstract-reports";
import { getEndPoint } from "../../../Utils/network.client";
import { API_END_POINT } from "../../../constants";
import axios from "axios";
import { PDFExport } from "@progress/kendo-react-pdf";
import DailyCashCollectionModel from "../../../models/reports/dailycashcollection-report";
import DailyCollectionModel from "../../../models/reports/dailycollection-report";
import { useGetCompanyDetails } from "../../../apiclient/master/useGetCompanydetailsApi";

interface IDailyCashCollectionReport {}

export const DailyCashCollectionReport: React.FC = () => {
  const [reportMenu, setReportMenu] = useState<IDailyCashCollectionReport[]>([]);
  const [fromDate, setFromDate] = useState<Date>(
    new Date(dayjs().add(0, "months").format("MM/DD/YYYY"))
  );
  const [toDate, setToDate] = useState<Date>(new Date());

  const exportExcel = useRef<ExcelExport | null>(null);
  const pdfExportRef = useRef<PDFExport | null>(null);

  const { data: companyDetails, isLoading: isCompanyLoading } =
    useGetCompanyDetails();
  const companyName = companyDetails?.[0]?.companyName || "Company Name";

  const [dailyCashCollectionList, setDailyCashCollectionList] = useState<
    DailyCashCollectionModel[]
  >([]);
  const [dailyCollectionList, setDailyCollectionList] = useState<
    DailyCollectionModel[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Refs for separate Excel exports
  const excelExportDailyCash = useRef<ExcelExport | null>(null);
  const excelExportDailyCollection = useRef<ExcelExport | null>(null);

  useEffect(() => {}, [reportMenu]);

  const exportBothExcelFiles = () => {
    const formattedDate = fromDate
      ? new Date(fromDate).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        })
      : "";

    if (excelExportDailyCash.current) {
      const optionsCash = excelExportDailyCash.current.workbookOptions();
      const sheetCash = optionsCash.sheets[0];
      // Insert report title with date heading second (so it will appear below company name)
      sheetCash.rows.unshift({
        cells: [
          {
            value: `Daily Cash Collection Report - ${formattedDate}`,
            colSpan: sheetCash.columns.length,
            bold: true,
            fontSize: 14,
            textAlign: "center",
          },
        ],
      });
      // Insert company name heading first (will appear on top)
      sheetCash.rows.unshift({
        cells: [
          {
            value: companyName,
            colSpan: sheetCash.columns.length,
            bold: true,
            fontSize: 14,
            textAlign: "center",
          },
        ],
      });

      excelExportDailyCash.current.save(optionsCash);
    }

    if (excelExportDailyCollection.current) {
      const optionsCollection = excelExportDailyCollection.current.workbookOptions();
      const sheetCollection = optionsCollection.sheets[0];
      // Insert report title with date heading second (so below company name)
      sheetCollection.rows.unshift({
        cells: [
          {
            value: `Daily Collection Report - ${formattedDate}`,
            colSpan: sheetCollection.columns.length,
            bold: true,
            fontSize: 14,
            textAlign: "center",
          },
        ],
      });
      // Insert company name heading first (top row)
      sheetCollection.rows.unshift({
        cells: [
          {
            value: companyName,
            colSpan: sheetCollection.columns.length,
            bold: true,
            fontSize: 14,
            textAlign: "center",
          },
        ],
      });

      excelExportDailyCollection.current.save(optionsCollection);
    }
  };

  const exportToPDF = () => {
    if (pdfExportRef.current) {
      pdfExportRef.current.save();
    }
  };

  const getOrderHistoryList = async () => {
    if (!fromDate || !toDate) {
      return;
    }

    try {
      setLoading(true); // Start loading before the API call
      const formattedFromDate = dayjs(fromDate).format("YYYY-MM-DD");
      const getDailyCashCollectionListUrl = `${getEndPoint(
        API_END_POINT.REPORT
      )}/api/DailyReportsService/GetDailyCashCollection/${formattedFromDate}`;

      const response = await axios.get(getDailyCashCollectionListUrl, {});

      const dailyCashCollectionList = response?.data?.result || [];
      console.log("a", dailyCashCollectionList);
      setDailyCashCollectionList(dailyCashCollectionList); // Update state with fetched data
      setLoading(false); // Stop loading when data is received
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false); // Stop loading if there's an error
    }
  };

  const getOrderSalesHistoryList = async () => {
    if (!fromDate || !toDate) {
      return;
    }

    try {
      setLoading(true); // Start loading before the API call
      const formattedFromDate = dayjs(fromDate).format("YYYY-MM-DD");
      const getDailyCollectionListUrl = `${getEndPoint(
        API_END_POINT.REPORT
      )}/api/DailyReportsService/GetDailyCollection/${formattedFromDate}`;

      const response = await axios.get(getDailyCollectionListUrl, {});

      const dailyCollectionList = response?.data?.result || [];
      console.log("b", dailyCollectionList);
      setDailyCollectionList(dailyCollectionList); // Update state with fetched data
      setLoading(false); // Stop loading when data is received
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false); // Stop loading if there's an error
    }
  };
  const hasRecords = dailyCashCollectionList.length > 0;

  let grid = (
    <Grid
      data={dailyCashCollectionList}
      groupable={false}
      sortable={true}
      columnVirtualization={false}
      resizable={true}
    >
      <GridColumn
        title="S.No"
        cell={(props) => {
          const index = props.dataItem
            ? dailyCashCollectionList.indexOf(props.dataItem) + 1
            : 0;
          return <td>{index}</td>;
        }}
      />
      <GridColumn field="zone" title="Zone" />

      <GridColumn field="orderDate" title="Order Date" />

      <GridColumn field="supplyDate" title="Supply Date" />

      <GridColumn field="mSales" title="Milk Sales" />

      <GridColumn field="curdSales" title="Curd Sales" />

      <GridColumn
        field="totLit"
        title="Total Liters"
        cell={(props) => (
          <td style={{ textAlign: "right" }}>{props.dataItem.totLit?.toFixed(2)}</td>
        )}
        footerCell={(props) => {
          const totalLiter = dailyCashCollectionList.reduce(
            (acc, item) => acc + (item.totLit || 0),
            0
          );
          return (
            <td colSpan={props.colSpan} style={{ textAlign: "right" }}>
              {totalLiter.toFixed(2)}
            </td>
          );
        }}
      />

      <GridNoRecords>
        <div className="mt-2" style={{ width: "100%", height: "500px" }}>
          <center>
            <h5>No data found</h5>
          </center>
        </div>
      </GridNoRecords>
    </Grid>
  );

  let seconeGrid = (
    <Grid
      style={{ marginTop: "0px" }}
      data={dailyCollectionList}
      groupable={false}
      sortable={false}
      columnVirtualization={false}
      resizable={true}
    >
      <GridColumn
        title="S.No"
        cell={(props) => {
          const index = props.dataItem
            ? dailyCollectionList.indexOf(props.dataItem) + 1
            : 0;
          return <td>{index}</td>;
        }}
      />
      <GridColumn field="category" title="Category" />

      <GridColumn field="code" title="Code" />

      <GridColumn field="agent" title="Agent" />

      <GridColumn field="toAmt" title="To Amount" />

      <GridColumn field="recptDate" title="Receipt Date" />

      <GridColumn field="recpt" title="Receipt" />

      <GridColumn field="inQty" title="In Qty" />

      <GridColumn field="iv" title="IV" />

      <GridColumn
        field="dBal"
        title="DBalance"
        cell={(props) => (
          <td style={{ textAlign: "right" }}>{props.dataItem.dBal?.toFixed(2)}</td>
        )}
        footerCell={(props) => {
          const DBalance = dailyCollectionList.reduce(
            (acc, item) => acc + (item.dBal || 0),
            0
          );
          return (
            <td colSpan={props.colSpan} style={{ textAlign: "right" }}>
              {DBalance.toFixed(2)}
            </td>
          );
        }}
      />

      <GridNoRecords>
        <div className="mt-2" style={{ width: "100%", height: "500px" }}>
          <center>
            <h5>No data found</h5>
          </center>
        </div>
      </GridNoRecords>
    </Grid>
  );

  return (
    <>
      <div
        className="m-lg-3 mt-lg-1 m-4 card p-3 border-0 m-3 sw-shadow radius-15"
        style={{ minHeight: "500px" }}
      >
        <div className="top-sec-align row">
          <center>
            <h4 className="text-normal title-web mb-0 report-title-color">
              Daily Cash Collection
            </h4>
          </center>
        </div>
        <div className="fade_rule"></div>
        <div className="d-flex justify-content-between mt-4 pt-3 pb-3">
          <div className="d-flex">
            <div className="me-2">
              <label>Select Date</label>
              <DatePicker
                selectsMultiple={true}
                openToDate={new Date()}
                className="form-control ms-auto search-right-align calendarBg"
                value={formatDate(fromDate, "dd/MM/yyyy")}
                placeholderText="To"
                dateFormat={"dd/MM/yyyy"}
                onChange={([date]: any) => {
                  setFromDate(date);
                }}
              />
            </div>
            <div>
              <button
                className="btn-primary btn search-btns mt-4"
                onClick={() => {
                  getOrderHistoryList();
                  getOrderSalesHistoryList();
                }}
                type="submit"
              >
                <svg width="17" height="17" viewBox="0 0 17.5 17.5">
                  <path
                    id="Path_magnify"
                    data-name="Path / magnify"
                    d="M9.5,3a6.5,6.5,0,0,1,4.94,10.73l.27.27h.79l5,5L19,20.5l-5-5v-.79l-.27-.27A6.5,6.5,0,1,1,9.5,3m0,2A4.5,4.5,0,1,0,14,9.5,4.481,4.481,0,0,0,9.5,5Z"
                    transform="translate(-3 -3)"
                    fill="#fff"
                  />
                </svg>
              </button>
            </div>
            <div className="d-flex justify-content-end">
              <button
                title="Export Excel"
                className="export-button ms-2"
                onClick={exportBothExcelFiles}
                disabled={!hasRecords}
                style={{ marginTop: "25px" }}
              >
                Export to Excel
              </button>
              <button
                title="Export Excel"
                className="export-button ms-2"
                onClick={exportToPDF}
                disabled={!hasRecords}
                style={{ marginTop: "25px" }}
              >
                Export to PDF
              </button>
            </div>
          </div>
        </div>
        <div className="row col=45">
          <LoadingIndicator isLoading={false} />
          <PDFExport
            fileName={"Daily_Cash_Collection_and_Collection_List.pdf"}
            ref={pdfExportRef}
            title="Daily Cash Collection"
          >
            <div style={{ textAlign: "center", marginBottom: "5px" }}>
              <span style={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                {companyDetails?.[0]?.companyName || "Company Name"}
              </span>
            </div>
            <div style={{ textAlign: "center", marginBottom: "10px" }}>
              <span style={{ fontWeight: "bold", fontSize: "1.25rem" }}>
                Daily Cash Collection Report
                {fromDate && (
                  <>
                    {" - "}
                    {new Date(fromDate).toLocaleDateString("en-IN", {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                    })}
                  </>
                )}
              </span>
            </div>

            {grid}
            <div style={{ textAlign: "center", marginBottom: "5px" }}>
              <span style={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                {companyDetails?.[0]?.companyName || "Company Name"}
              </span>
            </div>

            {/* Second Grid: Daily Collection */}
            <div style={{ textAlign: "center", marginBottom: "10px" }}>
              <span style={{ fontWeight: "bold", fontSize: "1.25rem" }}>
                Daily Collection
                {fromDate && (
                  <>
                    {" - "}
                    {new Date(fromDate).toLocaleDateString("en-IN", {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                    })}
                  </>
                )}
              </span>
            </div>
            {seconeGrid}
          </PDFExport>

          {/* First Excel Export for Daily Cash Collection */}
          <ExcelExport
            fileName={"Daily_Cash_Collection.xlsx"}
            data={dailyCashCollectionList} // Only Daily Cash Collection data
            ref={excelExportDailyCash}
          >
            {/* Columns for the Daily Cash Collection */}
            <ExcelExportColumn field="zone" title="Zone" />
            <ExcelExportColumn field="orderDate" title="Order Date" />
            <ExcelExportColumn field="supplyDate" title="Supply Date" />
            <ExcelExportColumn field="mSales" title="Milk Sales" />
            <ExcelExportColumn field="curdSales" title="Curd Sales" />
            <ExcelExportColumn 
            field="totLit" 
            title="Total Liters"
            footer={() => {
              const totalTotalLiter = dailyCashCollectionList.reduce(
                (acc, item) => acc + (item.totLit || 0),
                0
              );
              return totalTotalLiter.toFixed(2);
            }} 
            />
          </ExcelExport>

          {/* Second Excel Export for Daily Collection */}
          <ExcelExport
            fileName={"Daily_Collection.xlsx"}
            data={dailyCollectionList} // Only Daily Collection data
            ref={excelExportDailyCollection}
          >
            {/* Columns for the Daily Collection List */}
            <ExcelExportColumn field="category" title="Category" />
            <ExcelExportColumn field="code" title="Code" />
            <ExcelExportColumn field="agent" title="Agent" />
            <ExcelExportColumn field="toAmt" title="To Amount" />
            <ExcelExportColumn field="recptDate" title="Receipt Date" />
            <ExcelExportColumn field="recpt" title="Receipt" />
            <ExcelExportColumn field="inQty" title="In Qty" />
            <ExcelExportColumn field="iv" title="IV" />
            <ExcelExportColumn 
            field="dBal" 
            title="DBalance" 
            footer={() => {
              const totalTotalLiter = dailyCollectionList.reduce(
                (acc, item) => acc + (item.dBal || 0),
                0
              );
              return totalTotalLiter.toFixed(2);
            }} 
            />
          </ExcelExport>
        </div>
      </div>
    </>
  );
};

